import { ERRORS_MESSAGE } from "@lhd/constants";
import { Checkbox, Form, Input } from "antd";
import { FC, useEffect, useState } from "react";
import { AiOutlineEdit } from "react-icons/ai";
import styled from "styled-components";
import { EMPLOYEE_INFO_CHILDREN_SELECTION } from "../../../../constants";
import { ListColorPicker } from "../../../AppFormInformation";

type PropsType = {
  getFieldClassName?: (name: string) => string;
};

export const DEFAULT_EMPLOYEE_INFO_COLOR = "#174a83";

const normalizeHexColor = (color?: string) =>
  (color || "").trim().toLowerCase();

const isSameColor = (a?: string, b?: string) =>
  normalizeHexColor(a) === normalizeHexColor(b);

const isPresetColor = (color?: string) =>
  ListColorPicker.some((item) => isSameColor(item, color));

const ColorPickerStyles = styled.div`
  .wrap-color-picker {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    max-width: 320px;
  }

  .color-picker-item {
    padding: 4px;
    cursor: pointer;

    &.active {
      padding: 2px;
      border-radius: 50%;
      border: 2px solid rgba(0, 0, 0, 0.25);
    }
  }
  .color-picker-background {
    border-radius: 50%;
    width: 24px;
    height: 24px;
  }
  .custom-color-picker-item {
    position: relative;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    border-radius: 100%;
    box-sizing: content-box;
    border: 2px solid transparent;
    padding: 2px;

    &.active {
      padding: 2px;
      border: 2px solid rgba(0, 0, 0, 0.25);
    }

    input[type="color"] {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
      border: none;
      padding: 0;
      width: 100%;
      height: 100%;
    }

    svg {
      pointer-events: none;
      position: absolute;
      z-index: 1;
    }
  }

  .wrap-background-color-picked {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid transparent;
    vertical-align: middle;

    .background-color-picked {
      width: 14px;
      height: 14px;
      border-radius: 50%;
    }
  }
`;

const employeeInfoChildrenOptions = EMPLOYEE_INFO_CHILDREN_SELECTION.map(
  (item) => ({
    label: item.name,
    value: item.app_part_type,
  }),
);

const EmployeeInfoColorPicker = ({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  const selectedColor = value || DEFAULT_EMPLOYEE_INFO_COLOR;
  const usingCustomColor = !isPresetColor(selectedColor);
  const [customColor, setCustomColor] = useState(
    usingCustomColor ? selectedColor : DEFAULT_EMPLOYEE_INFO_COLOR,
  );

  useEffect(() => {
    if (usingCustomColor) {
      setCustomColor(selectedColor);
    }
  }, [usingCustomColor, selectedColor]);

  return (
    <ColorPickerStyles>
      <div
        className="wrap-background-color-picked"
        style={{ borderColor: selectedColor }}
      >
        <div
          className="background-color-picked"
          style={{ backgroundColor: selectedColor }}
        />
      </div>
      <div className="wrap-color-picker" style={{ marginTop: 8 }}>
        {ListColorPicker.map((backgroundColor: string) => (
          <div
            key={backgroundColor}
            className={`color-picker-item ${
              isSameColor(selectedColor, backgroundColor) ? "active" : ""
            }`}
            onClick={() => onChange?.(backgroundColor)}
          >
            <div
              className="color-picker-background"
              style={{ backgroundColor }}
            />
          </div>
        ))}
        <div
          className={`custom-color-picker-item ${
            usingCustomColor ? "active" : ""
          }`}
          title="カスタムカラー"
        >
          <div
            className="color-picker-background"
            style={{ backgroundColor: customColor }}
          >
            <input
              type="color"
              value={customColor}
              style={{ backgroundColor: customColor }}
              onChange={(e) => {
                const nextColor = e.target.value;
                setCustomColor(nextColor);
                onChange?.(nextColor);
              }}
              onClick={() => {
                // Re-select last custom color when switching back from a preset
                if (!usingCustomColor) {
                  onChange?.(customColor);
                }
              }}
            />
          </div>
          <AiOutlineEdit size={18} />
        </div>
      </div>
    </ColorPickerStyles>
  );
};

const EmployeeInfoSelectionSetting: FC<PropsType> = ({ getFieldClassName }) => {
  return (
    <>
      <Form.Item
        label="フィールド名"
        name="name"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("name")}`}
      >
        <Input />
      </Form.Item>
      <Form.Item
        valuePropName="checked"
        name="is_hide_name"
        style={{ marginBottom: "0.5rem" }}
      >
        <Checkbox>フィールド名を表示しない</Checkbox>
      </Form.Item>
      <Form.Item
        name={["item_config_data", "employee_info_color"]}
        label="カラー"
        initialValue={DEFAULT_EMPLOYEE_INFO_COLOR}
      >
        <EmployeeInfoColorPicker />
      </Form.Item>
      <Form.Item
        name={["item_config_data", "employee_info_children"]}
        label="選択した社員について、表示したい情報を選択してください（複数選択可能）"
      >
        <Checkbox.Group
          options={employeeInfoChildrenOptions}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        />
      </Form.Item>
      <Form.Item
        label="フィルードコード"
        name="field_code"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("field_code")}`}
      >
        <Input />
      </Form.Item>
    </>
  );
};

export default EmployeeInfoSelectionSetting;
